import { useEffect, useRef, useState } from "react";

interface VideoPlayerProps {
  cover: string;
  title: string;
  duration: number;
}

function format(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const pad = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

export default function VideoPlayer({ cover, title, duration }: VideoPlayerProps) {
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [volume, setVolume] = useState(70);
  const barRef = useRef<HTMLDivElement | null>(null);
  const shellRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!playing) return;
    const id = window.setInterval(() => {
      setCurrent((c) => {
        if (c + 1 >= duration) {
          setPlaying(false);
          return duration;
        }
        return c + 1;
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, [playing, duration]);

  const togglePlay = () => {
    if (current >= duration) setCurrent(0);
    setPlaying((p) => !p);
  };

  const seekFromEvent = (clientX: number) => {
    const el = barRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    setCurrent(Math.round(ratio * duration));
  };

  const toggleFullscreen = () => {
    const el = shellRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.().catch(() => undefined);
    } else {
      document.exitFullscreen?.().catch(() => undefined);
    }
  };

  const progress = duration > 0 ? (current / duration) * 100 : 0;

  return (
    <div
      ref={shellRef}
      className="group relative aspect-video w-full overflow-hidden rounded-lg border border-background-800 bg-background-950"
    >
      <img src={cover} alt={title} title={`${title} 主播视频`} className="h-full w-full object-cover object-top" />
      <div className="absolute inset-0 bg-background-950/45" />

      {!playing && (
        <button
          type="button"
          aria-label="播放"
          onClick={togglePlay}
          className="absolute inset-0 z-10 flex items-center justify-center"
        >
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-500 text-background-950 transition-transform hover:scale-105 md:h-20 md:w-20">
            <i className="ri-play-fill text-3xl md:text-4xl" />
          </span>
        </button>
      )}

      <span className="absolute left-3 top-3 z-10 flex items-center gap-1.5 rounded-full bg-background-950/80 px-3 py-1 text-xs font-semibold text-accent-300">
        <i className="ri-information-line" /> 演示播放器
      </span>

      <div className="absolute inset-x-0 bottom-0 z-20 bg-gradient-to-t from-background-950 via-background-950/70 to-transparent px-3 pb-3 pt-8 md:px-4 md:pb-4">
        <div
          ref={barRef}
          onClick={(e) => seekFromEvent(e.clientX)}
          className="group/bar flex cursor-pointer items-center py-2"
        >
          <div className="relative h-1.5 w-full rounded-full bg-background-600">
            <div className="absolute inset-y-0 left-0 rounded-full bg-primary-500" style={{ width: `${progress}%` }} />
            <div
              className="absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary-400 opacity-0 transition-opacity group-hover/bar:opacity-100"
              style={{ left: `${progress}%` }}
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            aria-label={playing ? "暂停" : "播放"}
            onClick={togglePlay}
            className="flex h-9 w-9 items-center justify-center rounded-full text-foreground-50 transition-colors hover:text-primary-400"
          >
            <i className={playing ? "ri-pause-fill text-2xl" : "ri-play-fill text-2xl"} />
          </button>
          <span className="font-label text-xs text-foreground-200 md:text-sm">
            {format(current)} / {format(duration)}
          </span>

          <div className="ml-auto flex items-center gap-3">
            <div className="hidden items-center gap-2 sm:flex">
              <i className="ri-volume-up-line text-foreground-200" />
              <input
                type="range"
                min={0}
                max={100}
                value={volume}
                onChange={(e) => setVolume(Number(e.target.value))}
                className="h-1 w-20 cursor-pointer accent-primary-500"
                aria-label="音量"
              />
            </div>
            <button
              type="button"
              aria-label="全屏"
              onClick={toggleFullscreen}
              className="flex h-9 w-9 items-center justify-center rounded-full text-foreground-200 transition-colors hover:text-primary-400"
            >
              <i className="ri-fullscreen-line text-lg" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}